import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  IconButton,
  Button,
  Alert,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BoltIcon from '@mui/icons-material/Bolt';
import BubbleChartIcon from '@mui/icons-material/BubbleChart';
import DirectionsRunIcon from '@mui/icons-material/DirectionsRun';
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import {
  GAME_TYPES,
  BLITZ_DURATIONS,
  KEY_POP_LEVELS,
} from './typingTutorGames';
import { fetchTypingProgress } from './typingTutorApi';
import { requireStudentAuth, parseStudentData } from './typingTutorUtils';
import { resolveTargetWpm } from './typingTutorCurriculum';
import './typingTutorTheme.css';

const ICON_MAP = {
  bolt: BoltIcon,
  bubble_chart: BubbleChartIcon,
  directions_run: DirectionsRunIcon,
};

export default function TypingTutorGamesHub() {
  const navigate = useNavigate();
  const [bestByGame, setBestByGame] = useState({});
  const student = parseStudentData();
  const targetWpm = resolveTargetWpm(student?.enrolled_courses);

  useEffect(() => {
    if (!requireStudentAuth(navigate)) return undefined;
    fetchTypingProgress()
      .then((p) => {
        const recent = p?.recentSessions || [];
        const bests = {};
        recent.forEach((s) => {
          const type = s.exercise_type;
          if (!['speed_blitz', 'key_pop', 'type_racer'].includes(type)) return;
          const wpm = Number(s.wpm) || 0;
          if (!bests[type] || wpm > bests[type]) bests[type] = wpm;
        });
        setBestByGame(bests);
      })
      .catch(() => {});
  }, [navigate]);

  const openGame = (gameId, variantId) => {
    navigate(`/student/typing-tutor/games/${gameId}/${variantId}`);
  };

  return (
    <Box className="tt-root">
      <Box className="tt-header">
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton onClick={() => navigate('/student/typing-tutor')} sx={{ color: '#fff' }}>
              <ArrowBackIcon />
            </IconButton>
            <SportsEsportsIcon sx={{ color: '#fff' }} />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h5" fontWeight={700}>
                Speed Games
              </Typography>
              <Typography variant="body2" className="tt-sans" sx={{ opacity: 0.9 }}>
                Short bursts to build speed — sessions sync to My Journey
              </Typography>
            </Box>
            <span className="tt-goal-badge">Goal: {targetWpm} WPM</span>
            <Button
              variant="outlined"
              size="small"
              startIcon={<EmojiEventsIcon />}
              onClick={() => navigate('/student/leaderboard')}
              sx={{ color: '#fff', borderColor: 'rgba(255,255,255,0.5)', ml: 1 }}
            >
              Toppers
            </Button>
          </Box>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Alert severity="info" className="tt-sans" sx={{ mb: 3 }}>
          Play games after fingering basics. Scores count toward your typing practice records.
        </Alert>

        {GAME_TYPES.map((game) => {
          const Icon = ICON_MAP[game.icon] || BoltIcon;
          const best = bestByGame[game.id];

          return (
            <Box key={game.id} className="tt-card" sx={{ p: 2.5, mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                <Box
                  sx={{
                    width: 52,
                    height: 52,
                    borderRadius: 2,
                    bgcolor: `${game.color}22`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Icon sx={{ fontSize: 28, color: game.color }} />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h6" fontWeight={700}>
                    {game.title}
                  </Typography>
                  <Typography variant="caption" className="tt-sans" sx={{ color: 'var(--tt-muted)' }}>
                    {game.subtitle}
                  </Typography>
                  <Typography variant="body2" className="tt-sans" sx={{ mt: 0.5 }}>
                    {game.description}
                  </Typography>
                  {best > 0 && (
                    <Typography variant="caption" className="tt-sans" sx={{ color: game.color, display: 'block', mt: 0.5 }}>
                      Your best: {Math.round(best)} WPM
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {game.id === 'speed_blitz' &&
                  BLITZ_DURATIONS.map((d) => (
                    <Button
                      key={d.id}
                      variant="contained"
                      className="tt-sans"
                      sx={{ bgcolor: game.color, '&:hover': { bgcolor: game.color, filter: 'brightness(0.9)' } }}
                      onClick={() => openGame(game.id, d.id)}
                    >
                      {d.label}
                    </Button>
                  ))}
                {game.id === 'key_pop' &&
                  KEY_POP_LEVELS.map((l) => (
                    <Button
                      key={l.id}
                      variant="contained"
                      className="tt-sans"
                      sx={{ bgcolor: game.color, '&:hover': { bgcolor: game.color, filter: 'brightness(0.9)' } }}
                      onClick={() => openGame(game.id, l.id)}
                    >
                      {l.title}
                    </Button>
                  ))}
                {game.id === 'type_racer' && (
                  <Button
                    variant="contained"
                    className="tt-sans"
                    sx={{ bgcolor: game.color, '&:hover': { bgcolor: game.color, filter: 'brightness(0.9)' } }}
                    onClick={() => openGame(game.id, 'racer-1')}
                  >
                    Race vs {targetWpm} WPM Ghost
                  </Button>
                )}
              </Box>
            </Box>
          );
        })}
      </Container>
    </Box>
  );
}
